import { useState, useEffect } from "react";
import ChatDetail from "../components/ChatDetail";
import ChatList from "../components/ChatList";
import { getConversationDetails, getConversations } from "../api/adminChatApi";
import { useLocation } from "react-router-dom";

const ChatMonitor = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [flagFilter, setFlagFilter] = useState("all");
  const [chatThreads, setChatThreads] = useState([]);
  const [chatMessages, setChatMessages] = useState({});
  const [selectedChatMessages, setSelectedChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const location = useLocation();
  const chatIdFromState = location.state?.chatId;

  // Fetch conversations on component mount
  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (chatThreads.length > 0 && chatIdFromState) {
      const chatToSelect = chatThreads.find(chat => chat.id === chatIdFromState);
      if (chatToSelect) {
        handleChatSelect(chatToSelect); // this fetches and sets chat messages
      }
    }
  }, [chatThreads, chatIdFromState]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getConversations();

      console.log("Conversation response", response);

      if (response.success) {
        // Transform user data into conversation format
        // This is a simplified approach - in a real scenario you'd want a dedicated admin endpoint
        const fetchedConversations = response.conversations.map((conv) => ({
          id: conv._id,
          users: conv.participants.map((p) => `${p.firstName} ${p.lastName}`),
          lastMessage: conv.lastMessage?.text || "No messages yet",
          timestamp: conv.updatedAt.slice(0, 16).replace("T", " "),
          flagged: conv.status === "flagged" || conv.isReported || false,
          participants: conv.participants.length,
        }));

        setChatThreads(fetchedConversations);
      } else {
        setError("Failed to fetch conversations");
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      setError("Error loading conversations: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatMessages = async (chatId) => {
    try {
      setLoading(true);
      const response = await getConversationDetails(chatId);

      console.log("fetch chat", response);
      

      if (response.success) {
        if (!response.conversation || !response.messages) {
        setError("No messages found in conversation");
        setSelectedChatMessages([]);
        return;
      }

        const messages = response.messages.map((msg) => ({
          id: msg._id,
          sender: msg.senderId?.firstName + " " + msg.senderId?.lastName || "Unknown",
          time: new Date(msg.createdAt).toLocaleString(),
          message: msg.text,
          flagged: msg.flagged || false,
        }));

        console.log("Mapped Messages", messages);
        

        setSelectedChatMessages(messages);
      } else {
        setError("Failed to fetch conversation details");
        setSelectedChatMessages([]);
      }
    } catch (error) {
      console.error("Error fetching chat messages:", error);
      setError("Error loading chat messages: " + error.message);
      setSelectedChatMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredChats = chatThreads.filter((chat) => {
    const matchesSearch =
      chat.users.some((user) =>
        user.toLowerCase().includes(searchTerm.toLowerCase())
      ) || chat.id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFlag =
      flagFilter === "all" ||
      (flagFilter === "flagged" && chat.flagged) ||
      (flagFilter === "unflagged" && !chat.flagged);

    return matchesSearch && matchesFlag;
  });

  const handleDeleteMessage = (messageId) => {
    console.log(`Delete message ${messageId}`);
  };

  const handleWarnUser = (username) => {
    console.log(`Warn user ${username}`);
  };

  const handleBanUser = (username) => {
    console.log(`Ban user ${username}`);
  };

  // Handle chat selection
  const handleChatSelect = async (chat) => {
    setSelectedChat(chat);
    await fetchChatMessages(chat.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading conversations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          <p>{error}</p>
          <button
            onClick={fetchConversations}
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (selectedChat) {
    return (
      <ChatDetail
        setSelectedChat={setSelectedChat}
        selectedChat={selectedChat}
        handleWarnUser={handleWarnUser}
        handleBanUser={handleBanUser}
        messages={selectedChatMessages}
        handleDeleteMessage={handleDeleteMessage}
      />
    );
  }

  return (
    <ChatList
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      flagFilter={flagFilter}
      setFlagFilter={setFlagFilter}
      filteredChats={filteredChats}
      setSelectedChat={handleChatSelect}
      handleWarnUser={handleWarnUser}
    />
  );
};

export default ChatMonitor;
